import type { ServiceQualitySheetView } from '@obraplan/core/application/use-cases/quality/quality-sheet-use-cases';
import {
  DAY_INSPECTION_RESULT_LABEL,
  LOCAL_STATUS_LABEL,
  localsMissingForTarget,
  type SheetLocal,
} from '@obraplan/core/domain/quality/service-quality-sheet';
import { formatNcNumbers, formatSheetDate, formatSheetQuantity } from './sheet-format';

/**
 * A ficha de qualidade em papel, no formato da FVS do cliente (Fase 24, RN-298, RN-299).
 *
 * ```text
 * FORM.109 · Execução de Alvenaria Não Estrutural
 * Obra · Serviço · Amostragem: 40% · Executado por · Responsável pela inspeção
 *
 * Item de verificação | Método | Tolerância |  1º Pav.            |  2º Pav.
 *                                            | Aprovado Reprovado | Aprovado Reprovado
 *
 * Considerações (locais pendentes de inspeção): 3º Pav., 4º Pav.
 * ```
 *
 * Só **a montagem** — uma função pura que recebe a ficha lida e devolve a definição do
 * documento — e, embaixo, quem carrega o `pdfmake` e baixa o arquivo. É a mesma separação do
 * RDO (ADR-124), e pelo mesmo motivo: o papel inteiro se testa sem navegador e sem biblioteca.
 *
 * ## Por que PDF, e não planilha
 *
 * A FVS é um **formulário** assinado e arquivado, como o RDO. O `pdfmake` já está no projeto
 * (e carrega sob demanda), e o formulário do cliente é uma grade de itens × pavimentos que o
 * PDF reproduz como está. Um `.xlsx` sairia sem dependência nova (o `fflate` do projeto
 * monta o zip), mas seria outro formato para manter igual ao papel — e a ficha é lida, e não
 * recalculada. Fica para quando alguém pedir para calcular em cima dela.
 *
 * ## Os pavimentos em blocos
 *
 * Cada pavimento ocupa duas colunas (Aprovado e Reprovado), e uma torre de vinte andares não
 * cabe numa página. A grade sai em **blocos de seis pavimentos**, repetindo os itens: quem
 * confere lê uma página inteira de cada vez, e não rola o papel de lado.
 */

interface Cell {
  readonly text?: string;
  readonly style?: string;
  readonly bold?: boolean;
  readonly italics?: boolean;
  readonly alignment?: 'left' | 'right' | 'center';
  readonly colSpan?: number;
  readonly rowSpan?: number;
  readonly fillColor?: string;
  readonly fontSize?: number;
  readonly stack?: readonly {
    readonly text: string;
    readonly fontSize?: number;
    readonly color?: string;
  }[];
}

type CellOrText = Cell | string;

interface TableNode {
  readonly table: {
    readonly headerRows: number;
    readonly widths: readonly (string | number)[];
    readonly body: readonly (readonly CellOrText[])[];
  };
  readonly margin?: readonly [number, number, number, number];
}

interface TextNode {
  readonly text: string;
  readonly style?: string;
  readonly bold?: boolean;
  readonly italics?: boolean;
  readonly fontSize?: number;
  readonly margin?: readonly [number, number, number, number];
}

export type SheetDocNode = TableNode | TextNode;

export interface SheetDocDefinition {
  readonly pageSize: 'A4';
  readonly pageOrientation: 'landscape';
  readonly pageMargins: readonly [number, number, number, number];
  readonly info: { readonly title: string };
  readonly defaultStyle: { readonly fontSize: number };
  readonly styles: Readonly<Record<string, Record<string, unknown>>>;
  readonly footer: (pagina: number, total: number) => TextNode;
  readonly content: readonly SheetDocNode[];
}

/** O cinza das barras e dos rótulos — o mesmo do RDO. */
const CINZA = '#e9e9e9';
const VAZIO = '';

/** Quantos pavimentos cabem lado a lado numa página paisagem, com Aprovado e Reprovado. */
export const LOCAIS_POR_BLOCO = 6;

function barra(texto: string, colunas: number): readonly CellOrText[] {
  return [
    { text: texto, style: 'barra', colSpan: colunas, alignment: 'center' as const },
    ...Array.from({ length: colunas - 1 }, () => VAZIO),
  ];
}

function cabecalho(...colunas: string[]): readonly CellOrText[] {
  return colunas.map((texto) => ({ text: texto, style: 'th', fillColor: CINZA }));
}

function emBlocos<T>(itens: readonly T[], tamanho: number): readonly (readonly T[])[] {
  const blocos: T[][] = [];
  for (let i = 0; i < itens.length; i += tamanho) blocos.push(itens.slice(i, i + tamanho));
  return blocos;
}

/** A tabela vazia diz que saiu vazia: seção que some deixa quem lê sem saber se foi omitida. */
function tabela(
  secao: string,
  colunas: readonly string[],
  larguras: readonly (string | number)[],
  linhas: readonly (readonly CellOrText[])[],
  vazio: string,
): TableNode {
  const corpo =
    linhas.length > 0
      ? linhas
      : [
          [
            { text: vazio, italics: true, colSpan: colunas.length },
            ...Array.from({ length: colunas.length - 1 }, () => VAZIO),
          ],
        ];
  return {
    table: {
      headerRows: 2,
      widths: larguras,
      body: [barra(secao, colunas.length), cabecalho(...colunas), ...corpo],
    },
    margin: [0, 0, 0, 10],
  };
}

export function buildQualitySheetDocument(view: ServiceQualitySheetView): SheetDocDefinition {
  const { sheet, service, checklist, project } = view;
  const unit =
    service.unitName === undefined
      ? undefined
      : { name: service.unitName, decimals: service.unitDecimals ?? 2 };
  const titulo =
    checklist.code === undefined ? checklist.name : `${checklist.code} · ${checklist.name}`;
  const missing = localsMissingForTarget(sheet.sampling);

  const campo = (rotulo: string, valor: string): readonly [Cell, Cell] => [
    { text: rotulo, style: 'rotulo', fillColor: CINZA },
    { text: valor },
  ];

  const conteudo: SheetDocNode[] = [
    {
      table: {
        headerRows: 0,
        widths: ['auto', '*', 'auto', '*'],
        body: [
          [
            {
              text: `Ficha de Verificação de Serviço — ${titulo}`,
              style: 'documento',
              colSpan: 4,
              alignment: 'center' as const,
              fillColor: CINZA,
            },
            VAZIO,
            VAZIO,
            VAZIO,
          ],
          [
            ...campo('Obra:', `${project.name} (${project.code})`),
            ...campo('Serviço:', service.name),
          ],
          [
            ...campo(
              'Amostragem:',
              checklist.samplingPct === undefined ? VAZIO : `${String(checklist.samplingPct)}%`,
            ),
            ...campo('Emitida em:', formatSheetDate(view.today)),
          ],
          [
            ...campo('Executado por:', sheet.executedBy.join(', ')),
            ...campo('Responsável pela inspeção:', sheet.inspectedBy.join(', ')),
          ],
          [
            { text: 'Inspeção:', style: 'rotulo', fillColor: CINZA },
            {
              text:
                `Inspecionados ${String(sheet.sampling.inspectedLocals)} de ` +
                `${String(sheet.sampling.executedLocals)} pavimentos executados ` +
                `(${String(sheet.sampling.percent)}%` +
                `${sheet.sampling.targetPct === undefined ? '' : `, meta ${String(sheet.sampling.targetPct)}%`}).` +
                (sheet.sampling.belowTarget
                  ? ` Abaixo da meta: ${missing === 1 ? 'falta' : 'faltam'} inspecionar ${String(missing)} ${missing === 1 ? 'pavimento' : 'pavimentos'}.`
                  : ''),
              colSpan: 3,
            },
            VAZIO,
            VAZIO,
          ],
        ],
      },
      margin: [0, 0, 0, 10],
    },
  ];

  // ── A ficha: itens × pavimentos, em blocos de seis ──────────────────────────────────
  const blocos: readonly (readonly SheetLocal[])[] =
    sheet.locals.length === 0 ? [[]] : emBlocos(sheet.locals, LOCAIS_POR_BLOCO);
  for (const bloco of blocos) {
    const colunas = 3 + bloco.length * 2;
    conteudo.push({
      table: {
        headerRows: 3,
        widths: ['*', '*', 60, ...bloco.flatMap(() => [46, 46])],
        body: [
          barra('VERIFICAÇÃO', colunas),
          [
            { text: 'Item de Verificação', style: 'th', fillColor: CINZA, rowSpan: 2 },
            { text: 'Método de Verificação', style: 'th', fillColor: CINZA, rowSpan: 2 },
            { text: 'Tolerância', style: 'th', fillColor: CINZA, rowSpan: 2 },
            ...bloco.flatMap((local): CellOrText[] => [
              {
                text: local.label,
                style: 'th',
                fillColor: CINZA,
                colSpan: 2,
                alignment: 'center' as const,
              },
              VAZIO,
            ]),
          ],
          [
            VAZIO,
            VAZIO,
            VAZIO,
            ...bloco.flatMap((): CellOrText[] => [
              { text: 'Aprovado', style: 'th', fillColor: CINZA, alignment: 'center' as const },
              { text: 'Reprovado', style: 'th', fillColor: CINZA, alignment: 'center' as const },
            ]),
          ],
          ...sheet.items.map((item): readonly CellOrText[] => [
            item.label,
            item.method ?? VAZIO,
            item.tolerance ?? 'NA',
            ...bloco.flatMap((local): CellOrText[] => {
              const cell = item.cells[local.activityId];
              return [
                { text: formatSheetDate(cell?.approvedOn), alignment: 'center' as const },
                {
                  text: cell === undefined ? VAZIO : formatNcNumbers(cell.ncNumbers),
                  alignment: 'center' as const,
                },
              ];
            }),
          ]),
        ],
      },
      margin: [0, 0, 0, 10],
    });
  }

  // ── As execuções por dia ────────────────────────────────────────────────────────────
  conteudo.push(
    tabela(
      'EXECUÇÕES POR DIA',
      ['Dia', 'Pavimento', 'Quantidade', 'Inspeção'],
      [60, '*', 90, '*'],
      sheet.executions.map((row): readonly CellOrText[] => [
        formatSheetDate(row.date),
        row.localLabel,
        { text: formatSheetQuantity(row.quantity, unit), alignment: 'right' as const },
        row.result === 'REPROVADO' && row.ncNumbers.length > 0
          ? `Reprovado (NC ${formatNcNumbers(row.ncNumbers)})`
          : DAY_INSPECTION_RESULT_LABEL[row.result],
      ]),
      'Nenhuma execução conferida deste serviço nesta obra.',
    ),
  );

  // ── A situação por pavimento ────────────────────────────────────────────────────────
  conteudo.push(
    tabela(
      'SITUAÇÃO POR PAVIMENTO',
      ['Pavimento', 'Situação', 'NCs', 'Falta inspecionar'],
      ['*', 90, 80, '*'],
      sheet.situations.map((situation): readonly CellOrText[] => [
        sheet.locals.find((local) => local.activityId === situation.activityId)?.label ?? VAZIO,
        LOCAL_STATUS_LABEL[situation.status],
        formatNcNumbers(situation.ncNumbers),
        situation.missingItems.join('; '),
      ]),
      'Nenhum pavimento executado.',
    ),
  );

  conteudo.push({
    table: {
      headerRows: 0,
      widths: ['*'],
      body: [
        [
          {
            stack: [
              { text: 'Considerações (locais pendentes de inspeção):', fontSize: 8 },
              {
                text:
                  sheet.pending.length === 0
                    ? 'Nenhum pavimento pendente de inspeção.'
                    : sheet.pending.map((local) => local.label).join(', '),
                fontSize: 8,
              },
            ],
          },
        ],
      ],
    },
    margin: [0, 0, 0, 16],
  });

  // A assinatura é impressa, e não assinada: a FVS é um papel que se assina na obra.
  conteudo.push({
    table: {
      headerRows: 0,
      widths: ['*', '*'],
      body: [
        [
          {
            stack: [
              { text: '_'.repeat(44), fontSize: 8 },
              { text: 'Executado por', fontSize: 8 },
            ],
          },
          {
            stack: [
              { text: '_'.repeat(44), fontSize: 8 },
              { text: 'Responsável pela inspeção', fontSize: 8 },
            ],
          },
        ],
      ],
    },
  });

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [36, 36, 36, 40],
    info: { title: `${titulo} — ${service.name} — ${project.name}` },
    defaultStyle: { fontSize: 8 },
    styles: {
      documento: { fontSize: 12, bold: true, margin: [0, 4, 0, 4] },
      barra: { fontSize: 9, bold: true, fillColor: CINZA, margin: [0, 2, 0, 2] },
      rotulo: { bold: true, alignment: 'right' },
      th: { bold: true, fontSize: 7.5 },
    },
    footer: (pagina, total) => ({
      text: `${titulo} · ${project.name} · ${formatSheetDate(view.today)} · página ${String(pagina)} de ${String(total)}`,
      fontSize: 7,
      margin: [36, 12, 36, 0],
    }),
    content: conteudo,
  };
}

/**
 * O nome do arquivo — `FVS-FORM-109-ASSENTAMENTO-DE-ALVENARIA-VIE-2026-09-18.pdf`.
 *
 * Padronizado pelo mesmo motivo do RDO: vira anexo de e-mail e linha de pasta, e é aí que o
 * nome passa a ser o índice. A ficha, o serviço e a obra dizem de que papel se trata; a data
 * fecha, porque a mesma ficha emitida em dois dias diz coisas diferentes.
 */
export function qualitySheetFileName(view: ServiceQualitySheetView): string {
  const limpa = (texto: string) =>
    texto
      .normalize('NFD')
      .replaceAll(/\p{Diacritic}/gu, '')
      .replaceAll(/[^A-Za-z0-9]+/gu, '-')
      .replaceAll(/^-|-$/gu, '')
      .toUpperCase();
  const partes = [
    'FVS',
    limpa(view.checklist.code ?? view.checklist.name),
    limpa(view.service.name),
    limpa(view.project.code),
    view.today,
  ].filter((parte) => parte !== '');
  return `${partes.join('-')}.pdf`;
}

/**
 * Carrega o `pdfmake` e baixa o arquivo.
 *
 * O `import()` é dinâmico pelo motivo do RDO: a biblioteca e as fontes somam mais de um
 * megabyte, e a tela é aberta muito mais vezes do que o papel é emitido.
 */
export async function emitirPdfDaFicha(view: ServiceQualitySheetView): Promise<void> {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);

  pdfMake.addVirtualFileSystem(vfs);
  await pdfMake.createPdf(buildQualitySheetDocument(view)).download(qualitySheetFileName(view));
}
